import { useState, useRef, useCallback } from 'react';
import { audioService } from '../services/api';

export const useAudioStream = (fileId) => {
  const [bufferState, setBufferState] = useState('initializing');
  const [statusMessage, setStatusMessage] = useState('Initializing...');
  const [analyser, setAnalyser] = useState(null);
  
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  const cleanupAudio = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setBufferState('initializing');
    setAnalyser(null);
  }, []);

  const startStreaming = useCallback(async () => {
    try {
      setBufferState('connecting');
      setStatusMessage('Preparing audio stream...');

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const audio = new Audio();
      audioRef.current = audio;
      
      let isFirstError = true;
      
      const setupAudioEvents = () => {
        audio.addEventListener('canplay', async () => {
          try {
            setStatusMessage('Starting playback...');
            const analyserNode = audioContextRef.current.createAnalyser();
            analyserNode.fftSize = 128;
            
            const source = audioContextRef.current.createMediaElementSource(audio);
            source.connect(analyserNode);
            analyserNode.connect(audioContextRef.current.destination);
            
            setAnalyser(analyserNode);
            await audio.play();
            setBufferState('ready');
            setStatusMessage('Playing');
          } catch (playError) {
            setStatusMessage('Buffering...');
          }
        });
        
        audio.addEventListener('waiting', () => {
          setBufferState('buffering');
          setStatusMessage('Buffering...');
        });
        
        audio.addEventListener('playing', () => {
          setBufferState('ready');
          setStatusMessage('Playing');
        });
        
        audio.addEventListener('ended', () => {
          setBufferState('complete');
          setStatusMessage('Playback complete');
        });
        
        audio.addEventListener('error', () => {
          if (!isFirstError) {
            setStatusMessage('Buffering...');
          }
          isFirstError = false;
          
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }
          
          retryTimeoutRef.current = setTimeout(() => {
            if (audioRef.current) {
              audioRef.current.load();
            }
          }, 1000);
        });
      };

      setupAudioEvents();
      
      audio.crossOrigin = "anonymous";
      audio.src = audioService.getStreamUrl(fileId);
      audio.load();
      
    } catch (error) {
      setStatusMessage('Buffering...');
    }
  }, [fileId]);

  return {
    bufferState,
    statusMessage,
    analyser,
    startStreaming,
    cleanupAudio
  };
};